import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, pairwise, switchMap, take, takeUntil } from 'rxjs/operators';
import { AuthActions } from './auth/actions';
import * as fromAuth from './auth/reducers';
import { CustomerFacade } from './facades/customer.facade';
import { SeoFacade } from './facades/seo.facade';
import { StoreConfigurationFacade } from './facades/store-configuration.facade';
import { StoreSelectionFacade } from './facades/store-selection.facade';
import { I18nService } from './services';
import { AppUtils } from './utils/app.utils';
import { LocalStorageUtils } from './utils/localStorage.utils';
import { UrlUtils } from './utils/url.utils';
import { environment } from '../environments/environment';
import { CmsFacade } from './facades/cms.facade';
import { CatalogActions, GuestCartActions } from './actions';
import { EFeatureToggles } from './configurations/common';
import { ConfigurationFacade } from './facades/configuration.facade';
import { PlaywrightBridgeService } from './services';
import { combineLatest, Subject } from 'rxjs';
import { FooterFacade } from './facades/footer.facade';
import { MarketingFacade } from './facades/marketing.facade';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  showFullPageLoader = false;
  fullPageLoaderList = [];
  isExternalQuoteCheckoutLoaded: boolean = false;
  areAllDataLoadedForCheckout: boolean = true;

  private unsubscribe$ = new Subject<void>();

  private readonly store: Store<fromAuth.State> = inject<Store<fromAuth.State>>(Store);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly seoFacade: SeoFacade = inject(SeoFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly i18nService: I18nService = inject(I18nService);
  private readonly storeSelectionFacade: StoreSelectionFacade = inject(StoreSelectionFacade);
  private readonly storeConfigurationFacade: StoreConfigurationFacade = inject(StoreConfigurationFacade);
  private readonly cmsFacade: CmsFacade = inject(CmsFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly footerFacade: FooterFacade = inject(FooterFacade);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly document: Document = inject(DOCUMENT);
  private readonly _playwrightBridgeService: PlaywrightBridgeService = inject(PlaywrightBridgeService);

  /**
   * Main application component that initializes the application state and handles routing.
   */
  constructor() {

    // Set SEO metadata for the page.
    this.seoFacade.setSeoForPage();

    // Listen to router events to handle route changes.
    this.router.events.pipe(
      filter(event => event instanceof RoutesRecognized),
      pairwise(), // Pair the previous and current route events.
    ).subscribe((routes: any[]) => {

      /**
       * @description
       * Extracts the locale from the URL path of the previous and current routes.
       * @type {string}
       */
      const previousLocale: string = UrlUtils.getLocaleFromPathName(routes[0].url);
      const currentLocale: string = UrlUtils.getLocaleFromPathName(routes[1].url);

      /**
       * @description
       * Extracts the market from the locale.
       * This is used to determine if the user has changed their market.
       * @type {string}
       * @note
       * The market is derived from the locale by splitting the locale string.
       * For example, if the locale is 'en-US', the market will be 'US'.
       * This is useful for determining if the user has switched between different markets (e.g., EU, JP).
       */
      const [_languagePrevious, previousMarket] = previousLocale.split('-');
      const [_languageCurrent, currentMarket] = currentLocale.split('-');

      /**
       * @description
       * Reload the page if the market has changed.
       * This is necessary to ensure that the application
       * is correctly initialized with the new market settings.
       * Also checks if the previous market exists or is just typo.
       *
       * @example
       * If the user navigates to a different market (e.g., from EU to JP),
       * the page will reload to apply the new market settings.
       *
       * @note
       * Uses DOCUMENT.location.reload() instead of window to keep the code testable and SSR-safe
       */
      if (!previousMarket || AppUtils.checkIfStoreIdExists(previousMarket)) {
        if (previousMarket !== currentMarket && currentLocale !== '') {
          this.document.location.reload();
        }
      }

      // Skip processing if the routes are the same.
      if (UrlUtils.isTheSameRoute(routes)) {
        return;
      }

      // Set the previous page in local storage.
      LocalStorageUtils.setKeyValue('previousPage', routes[0].url);
    });
  }

  /**
   * Guest application mode is activated in case of user is on EU store, and then
   * we need to reduce unnecessary calls and silent login from TpF.
   */
  ngOnInit(): void {
    this._selectAreAllDataLoadedForCheckout();

    this.configurationFacade.isFeatureEnabled(EFeatureToggles.SIPAYCE_INTEGRATION).pipe(
      take(1),
    ).subscribe(isSipayceIntegrationEnabled => {
      if (isSipayceIntegrationEnabled) {
        this._addPaymentJsScript();
      }
    });

    this.configurationFacade.isFeatureEnabled(EFeatureToggles.EXTERNAL_QUOTE_CHECKOUT).pipe(
      take(1),
    ).subscribe(isExternalQuoteCheckoutEnabled => {

      //dispatching actions to fetch footer navigation and social media footer
      this.footerFacade.dispatchFetchFooterNavigation();
      this.footerFacade.dispatchFetchSocialMediaFooter();

      if (!isExternalQuoteCheckoutEnabled) {
        this.store.dispatch(AuthActions.handleAuth());
        this.store.dispatch(CatalogActions.loadHowManyProductShouldBeCached());
      } else {
        this.store.dispatch(AuthActions.setIsLoginInProcess({isLoginInProcess: false}));
      }

      this._handleRouteParamsAndData(isExternalQuoteCheckoutEnabled);
      this.storeConfigurationFacade.dispatchFetchStoreConfiguration(
        AppUtils.getCurrentStore().storeId,
      );

      if (!isExternalQuoteCheckoutEnabled) {
        this.cmsFacade.getHomepageContentBannerData(environment.homePageBannerKey);
        this.cmsFacade.getStoreSelectionContentBannerData(environment.storeSelectionBannerKey);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Handles reactive subscription to router events and updates component state based on route params and data.
   *
   * This method listens for `NavigationEnd` events and extracts the deepest child route to access
   * parameters, route data, and the current URL. Based on this information:
   *
   * - It sets the store locale using `customerFacade`.
   * - Optionally triggers guest cart loading for external quote checkout scenarios.
   * - Updates full-page loader UI flags from route data.
   *
   * @param isExternalQuoteCheckoutEnabled
   * @private
   */
  private _handleRouteParamsAndData(isExternalQuoteCheckoutEnabled: boolean): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this._getDeepestRoute(this.route)),
      switchMap(currentRoute =>
        combineLatest([currentRoute.paramMap, currentRoute.data, currentRoute.url]).pipe(
          map(([paramMap, data, url]) => {
            const params = Object.fromEntries(paramMap.keys.map(key => [key, paramMap.get(key)]));
            return {params, data, url: url.map(url => url.path).join('/')};
          }),
        ),
      ),
    ).subscribe(({params, data, url}) => {
      const defaultLocale = this.i18nService.getCurrentLocale();
      const store = params?.lang || defaultLocale;

      this.customerFacade.setStore(store);

      if (
        isExternalQuoteCheckoutEnabled &&
        this._isGuestCartRoute(url) &&
        params?.guestUserId &&
        params?.guestCartId &&
        !this.isExternalQuoteCheckoutLoaded
      ) {
        this.store.dispatch(GuestCartActions.loadGuestCart({
          guestUserId: params.guestUserId,
          guestCartId: params.guestCartId,
        }));
        this.isExternalQuoteCheckoutLoaded = true;
      }

      this.showFullPageLoader = data?.['fullPageLoader'] ?? false;
      this.fullPageLoaderList = data?.['fullPageLoaderList'] ?? [];
    });
  }

  /**
   * Get the deepest route in the router tree.
   *
   * @param {ActivatedRoute} route
   * @returns {ActivatedRoute}
   * @private
   */
  private _getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    return route.firstChild ? this._getDeepestRoute(route.firstChild) : route;
  }

  /**
   * Check if user is on store selection page.
   *
   * @returns {boolean}
   */
  isOnStoreSelection(): boolean {
    return this.storeSelectionFacade.isOnStoreSelectionPage();
  }

  /**
   * Check if current route is a guest cart route.
   *
   * @private
   * @param {string} url
   * @returns {boolean}
   */
  private _isGuestCartRoute(url: string): boolean {
    return environment.guestCartsRoutes.some(route => url?.includes(route));
  }

  /**
   * selects whether all data from cart checkout is loaded.
   * @private
   */
  private _selectAreAllDataLoadedForCheckout(): void {
    this.marketingFacade.selectAreAllDataLoadedForCheckout().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(
      (areAllDataLoaded: boolean) => {
        this.areAllDataLoadedForCheckout = areAllDataLoaded;
      },
    );
  }

  /**
   * Adds paymentJs script for Sipayce payment integration.
   *
   * @private
   */
  private _addPaymentJsScript(): void {
    const script = document.createElement('script');
    script.src = environment.paymentJsUrl;
    script.setAttribute('data-main', 'payment-js');
    script.async = true;
    document.body.appendChild(script);
  }
}
